<template>
    <div class="h-6">
        <BadgePill :color="finalGrade.color" v-if="finalGrade.is_graded">
            {{ finalGrade.letter }}
        </BadgePill>
    </div>
    <div class="bg-gray-100 overflow-hidden rounded-lg mt-3">
        <div class="px-4 py-5 sm:p-6">
            <div class="flex flex-col mt-5" v-if="rubric.subactivities.length > 0">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200 table-fixed w-full">
                            <thead>
                                <tr>
                                    <th
                                        class="hidden md:table-cell max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                    >
                                        -
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-show="rubric.rubric_level_zero"
                                    >
                                        0
                                    </th>
                                    <th
                                        class="max-w-xs px-2 py-1 md:px-6 md:py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-for="i in rubric.rubric_levels"
                                        :key="i"
                                    >
                                        {{ i }}
                                    </th>
                                    <th
                                        class="hidden md:table-cell max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    ></th>
                                </tr>
                            </thead>
                            <tbody v-for="subactivity in rubric.subactivities" :key="subactivity.id">
                                <template v-for="(line, lineIndex) in subactivity.lines" :key="lineIndex">
                                    <tr
                                        class="text-sm leading-5 text-gray-500 table-row sm:hidden"
                                        :class="line.index % 2 !== 0 ? 'bg-white' : 'bg-gray-50'"
                                    >
                                        <td :colspan="rubric.rubric_levels + (rubric.rubric_level_zero ? 1 : 0)">
                                            {{ line.name }}
                                        </td>
                                    </tr>
                                    <tr :class="line.index % 2 !== 0 ? 'bg-white' : 'bg-gray-50'">
                                        <td class="hidden md:table-cell px-6 py-4 text-sm leading-5 text-gray-500">
                                            {{ line.name }}
                                        </td>
                                        <td
                                            v-for="level in line.levels"
                                            class="px-1 py-1 text-sm leading-5 text-gray-500 cursor-pointer border"
                                            :class="
                                                line.level === level.level
                                                    ? getLevelClasses(level, rubric.rubric_levels)
                                                    : ''
                                            "
                                            :key="`${lineIndex}_${level.level}`"
                                            @click="toggleLevel(line, level.level)"
                                            v-show="level.level !== 0 || rubric.rubric_level_zero"
                                        >
                                            <div class="block md:hidden h-6 pointer-events-none" />
                                            <label class="pointer-events-none">
                                                <input
                                                    type="radio"
                                                    :name="`${line.id}_${level.level}`"
                                                    :value="level.level"
                                                    v-model="line.level"
                                                    class="sr-only"
                                                />
                                                <span class="cursor-pointer hidden sm:inline-block pointer-events-none">
                                                    {{ level.description }}
                                                </span>
                                            </label>
                                        </td>
                                        <td class="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm leading-5">
                                            <button
                                                type="button"
                                                @click="line.level = null"
                                                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-400 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                                                :class="line.level !== null ? 'visible' : 'invisible'"
                                            >
                                                <svg
                                                    class="w-6 h-6"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="block sm:hidden">
        <div v-for="subactivity in rubric.subactivities" :key="subactivity.id">
            <div v-for="(line, lineIndex) in subactivity.lines" :key="lineIndex" class="mt-3">
                <div class="font-semibold text-gray-900">{{ line.name }}</div>
                <div v-for="level in line.levels" :key="level.level">
                    <div
                        class="flex flex-col p-4 mt-3 border border-2 list-group-item cursor-pointer"
                        v-show="level.level !== 0 || rubric.rubric_level_zero"
                        :key="`${lineIndex}_${level.level}`"
                        @click="toggleLevel(line, level.level)"
                        :class="
                            line.level === level.level ? getLevelClasses(level, rubric.rubric_levels) : 'bg-gray-50'
                        "
                    >
                        <div class="">
                            {{ level.description }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="flex flex-col mt-5 hidden">
        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div
                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
            >
                <table class="divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th
                                class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                            >
                                -
                            </th>
                            <th
                                class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.score') }}
                            </th>
                            <th
                                class="max-w-xs px-6 py-3 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                d10
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            :class="lineIndex % 2 === 0 ? 'bg-white' : 'bg-gray-200'"
                            v-for="(line, lineIndex) in lineGrades"
                            :key="line.id"
                        >
                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                {{ line.name }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                {{ line.level }} / {{ rubric.rubric_levels }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                {{ line.decimal_score }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
<script setup>
import BadgePill from '@/components/BadgePill.vue';
import GradeTranslator from '@/classes/GradeTranslator.js';
import { computed, ref } from 'vue';

const props = defineProps({
    modelValue: {},
    ranges: {
        required: true,
    },
});

const rubric = ref(props.modelValue);

const gradeTranslator = new GradeTranslator(props.ranges);

const getLevelClasses = (level, rubricLevels) => {
    const grade = (level.level / rubricLevels) * 10;
    return gradeTranslator.scoreToPillClasses(grade);
};

const lineGrades = computed(() => {
    return rubric.value.subactivities
        .flatMap((subactivity) => subactivity.lines)
        .map((line) => {
            const grade = (line.level / rubric.value.rubric_levels) * 10;
            return {
                id: line.id,
                name: line.name,
                level: line.level,
                decimal_score: line.level === null ? line.level : Math.round(grade * 10) / 10,
            };
        });
});

const finalGrade = computed(() => {
    const total = lineGrades.value
        .filter((l) => l.level !== null)
        .map((l) => l.level)
        .reduce((a, b) => a + b, 0);

    const maxTotal = lineGrades.value.length * rubric.value.rubric_levels;
    const decimalScore = (total / maxTotal) * 10;
    return {
        is_graded: !!lineGrades.value.filter((l) => l.level !== null).length,
        total: total,
        maxTotal: maxTotal,
        decimal_score: decimalScore,
        letter: gradeTranslator.scoreToLetter(decimalScore),
        classes: gradeTranslator.scoreToPillClasses(decimalScore),
        color: gradeTranslator.scoreToColor(decimalScore),
    };
});

function toggleLevel(line, level) {
    if (line.level === level) {
        line.level = null;
    } else {
        line.level = level;
    }
}
</script>
